import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { Avatar, Box, Button, Container, Typography } from '@mui/material';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import VerifiedIcon from '@mui/icons-material/Verified';
import { green, red } from '@mui/material/colors';
import { recoveryPasswordFields, SUBMIT_BTN_TYPE } from '../Form/form.utils';
import { FormField } from '../../lib/interfaces/Form';
import Form from '../Form/Form';
import { UserRecoveryPasswordDTO } from '../../lib/interfaces/User';
import { setLoginModalOpen } from '../../redux/reducers/ui/modalsReducer';
import { recoveryPassword } from '../../api/apis';

const RecoveryPasswordLayout: FC = () => {
	const { refreshToken = '' } = useParams();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const recoveryPasswordFormFields = recoveryPasswordFields.map(
		(field: FormField) => {
			if (field.label) {
				field.label = t(field.label);
			}
			return field;
		},
	);

	const recoveryPasswordMutation = useMutation<any, AxiosError<any>, string>(
		(newPassword: any) =>
			recoveryPassword({
				recoverPasswordToken: refreshToken,
				password: newPassword,
			}),
	);

	const handleSubmit = (formValuesFP: UserRecoveryPasswordDTO) => {
		recoveryPasswordMutation.mutate(formValuesFP.password);
	};

	const content: React.ReactElement = (
		<Container component="main" maxWidth="xs">
			<div className="AuthModal__wrapper">
				<div className="AuthModal__title">
					<Avatar className="AuthModal__avatar">
						<ReplayCircleFilledIcon />
					</Avatar>
					<Typography component="h1" variant="h5">
						{t('createNewPassword')}
					</Typography>
					{recoveryPasswordMutation.isError && (
						<Typography color={red[500]}>
							{recoveryPasswordMutation?.error?.response?.data?.message}
						</Typography>
					)}
				</div>
				<Form
					fields={recoveryPasswordFormFields}
					onSubmit={handleSubmit}
					saveButtonName={t('send')}
					isLoading={recoveryPasswordMutation.isLoading}
					saveButtonStyle={SUBMIT_BTN_TYPE.FULL}
					fullWidth
				/>
			</div>
		</Container>
	);

	const successContent: React.ReactElement = (
		<Container component="main" maxWidth="xs">
			<div className="AuthModal__wrapper">
				<div className="AuthModal__title">
					<Avatar color={green[500]} className="AuthModal__avatar success">
						<VerifiedIcon />
					</Avatar>
					<Typography component="h1" variant="h5" sx={{ textAlign: 'center' }}>
						{t('newPasswordSuccessfullyRecovered')}
					</Typography>
					<Typography sx={{ textAlign: 'center' }}>
						{recoveryPasswordMutation.data?.msg}
					</Typography>
				</div>
				<Box>
					<Button
						disableElevation
						variant="contained"
						onClick={() => dispatch(setLoginModalOpen(true))}
					>
						{t('login')}
					</Button>
				</Box>
			</div>
		</Container>
	);

	return (
		<Container
			sx={{
				height: '100vh',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
			}}
		>
			{recoveryPasswordMutation.isSuccess ? successContent : content}
		</Container>
	);
};

export default RecoveryPasswordLayout;
